// Cross-meeting transcript search (docs/product-specs/search.md).
// Snippets are server-escaped; the only markup is <mark>.

import { html, LitElement } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { ApiError, search } from "../lib/api";
import { navigate } from "../lib/store";
import { fmtMs, type SearchHit } from "../lib/types";

export class MmSearch extends LitElement {
  static override properties = {
    route: { attribute: false },
  };

  route = "";

  private hits: SearchHit[] = [];
  private query = "";
  private searched = false;
  private error = "";

  override createRenderRoot(): HTMLElement {
    return this;
  }

  private async run(e: Event): Promise<void> {
    e.preventDefault();
    const input = this.querySelector("input[name=q]") as HTMLInputElement;
    this.query = input.value.trim();
    if (this.query.length < 2) return;
    try {
      this.hits = await search(this.query);
      this.error = "";
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : "search failed";
    }
    this.searched = true;
    this.requestUpdate();
  }

  private open(hit: SearchHit): void {
    navigate(`/meetings/${hit.meeting_id}?t=${hit.start_ms}`);
  }

  override render() {
    return html`
      <section class="summary">
        <p class="eyebrow">Workspace</p>
        <h1>Search transcripts</h1>
        <form class="toolbar" @submit=${this.run}>
          <input
            name="q"
            type="search"
            placeholder="e.g. budget approval"
            class="grow"
            minlength="2"
            required
          />
          <button class="primary-button">Search</button>
        </form>
      </section>
      ${this.error
        ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
        : ""}
      <section>
        ${!this.searched
          ? html`<p class="empty">
              Results come from every meeting you own — transcripts are
              indexed automatically.
            </p>`
          : this.hits.length === 0
            ? html`<p class="empty">No matches for “${this.query}”.</p>`
            : html`<div class="timeline">
                ${this.hits.map(
                  (h) => html`
                    <div
                      class="timeline-item search-hit"
                      @click=${() => this.open(h)}
                    >
                      <div class="timeline-head">
                        <strong>${h.meeting_title}</strong>
                        <span class="transcript-time">${fmtMs(h.start_ms)}</span>
                      </div>
                      <p class="muted">…${unsafeHTML(h.snippet)}…</p>
                    </div>
                  `,
                )}
              </div>`}
      </section>
    `;
  }
}

customElements.define("mm-search", MmSearch);
